export type DateInput = Date | number | string;

export function timeAgo(date: DateInput, locale?: string): string {
	const seconds = Math.max(
		0,
		Math.floor((Date.now() - new Date(date).getTime()) / 1000),
	);

	const isId = locale === "id";

	if (seconds < 60)
		return isId ? `${seconds} dtk yang lalu` : `${seconds}s ago`;

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return isId ? `${minutes}m yang lalu` : `${minutes}m ago`;

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return isId ? `${hours}j yang lalu` : `${hours}h ago`;

	const days = Math.floor(hours / 24);
	if (days < 30) return isId ? `${days}h yang lalu` : `${days}d ago`;

	const months = Math.floor(days / 30);
	if (months < 12) return isId ? `${months} bln yang lalu` : `${months}mo ago`;

	return isId
		? `${Math.floor(months / 12)} thn yang lalu`
		: `${Math.floor(months / 12)}y ago`;
}

export function formatDateLabel(date: DateInput, locale?: string): string {
	return new Intl.DateTimeFormat(locale === "id" ? "id-ID" : "en-US", {
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	}).format(new Date(date));
}

/** Display prefix per ISO currency code. Shared by every salary formatter. */
export const CURRENCY_SYMBOLS: Record<string, string> = {
	USD: "$",
	EUR: "€",
	GBP: "£",
	CHF: "CHF ",
	SGD: "S$",
	CAD: "CA$",
	AUD: "A$",
	NZD: "NZ$",
	JPY: "¥",
	HKD: "HK$",
	NOK: "kr",
	SEK: "kr",
	DKK: "kr",
	IDR: "Rp",
};

export function formatPayRange(
	min: number | null | undefined,
	max: number | null | undefined,
	currency: string | null | undefined,
	period: string | null | undefined,
): string | null {
	/**
	 * Compact thousands, to at most ONE decimal place.
	 *
	 * `${amount / 1000}k` printed the raw float, so 33_333 rendered as
	 * "33.333k" and 81_499 as "81.499k". In English that is merely noisy; on an
	 * Indonesian page it is actively wrong, because id-ID uses "." as the
	 * THOUSANDS separator — "$33.333k/mo" reads as "$33,333k per month" to the
	 * audience this site is built for, on the one figure the whole product is
	 * pitched on. The degenerate-range comment below ("$185.5k–185.5k/yr")
	 * shows one decimal was always the intended shape; it was never enforced.
	 *
	 * A whole number keeps no decimal at all: 50_000 is "50k", not "50.0k".
	 */
	const isIdr = currency?.toUpperCase() === "IDR";
	const formatCurrency = (amount: number) => {
		// IDR figures are millions: "Rp3000k" is unreadable to the Indonesian
		// audience, who quote pay in juta ("3 jt"). Same juta unit as
		// formatFacetSalaryBand (src/lib/seo/llms-stats.ts).
		if (isIdr && amount >= 1_000_000) {
			const juta = Math.round(amount / 100_000) / 10;
			return `${juta}jt`;
		}
		if (amount >= 1000) {
			const thousands = Math.round(amount / 100) / 10;
			return `${thousands}k`;
		}
		return amount.toString();
	};

	const symbol =
		(currency ? CURRENCY_SYMBOLS[currency.toUpperCase()] : null) ?? "$";

	let periodStr = "";
	if (period === "hourly") periodStr = "/hr";
	else if (period === "monthly") periodStr = "/mo";
	else if (period === "yearly") periodStr = "/yr";

	if (min && max) {
		// A degenerate range (min === max) reads as a data error ("$185.5k–185.5k/yr")
		// — render the single figure instead.
		if (min === max) {
			return `${symbol}${formatCurrency(min)}${periodStr}`;
		}
		return `${symbol}${formatCurrency(min)}–${formatCurrency(max)}${periodStr}`;
	}
	if (min) {
		return `${symbol}${formatCurrency(min)}+${periodStr}`;
	}
	if (max) {
		return `Up to ${symbol}${formatCurrency(max)}${periodStr}`;
	}

	return null;
}

export function slugify(text: string): string {
	return text
		.toString()
		.toLowerCase()
		.trim()
		.replace(/\s+/g, "-") // Replace spaces with -
		.replace(/[^\w-]+/g, "") // Remove all non-word chars
		.replace(/--+/g, "-") // Replace multiple - with single -
		.replace(/^-+/, "") // Trim - from start of text
		.replace(/-+$/, ""); // Trim - from end of text
}

/**
 * One salary figure in its ORIGINAL currency, locale-grouped: "Rp3,000,000"
 * (en), "Rp 3.000.000" (id), "$3,000". Never converts and never assumes USD
 * for a non-USD figure — a bare "$" on an IDR amount is the 2026-10-27 owner
 * report (/en/companies/ruangguru showed "$3,000,000/mo"). Unknown codes
 * render as "PHP 30,000".
 */
export function formatMoneyAmount(
	amount: number,
	currency: string,
	locale: "id" | "en",
): string {
	const code = currency.trim().toUpperCase();
	const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `;
	const grouped = Math.round(amount).toLocaleString(
		locale === "id" ? "id-ID" : "en-US",
	);
	// Indonesian convention writes rupiah with a space: "Rp 3.000.000".
	const gap = locale === "id" && code === "IDR" ? " " : "";
	return `${symbol}${gap}${grouped}`;
}

/** {@link formatMoneyAmount} plus the per-month suffix ("/bln" id, "/mo" en). */
export function formatMonthlyAmount(
	amount: number,
	currency: string,
	locale: "id" | "en",
): string {
	return `${formatMoneyAmount(amount, currency, locale)}${locale === "id" ? "/bln" : "/mo"}`;
}
